import React, { useEffect, useRef, useState } from 'react';
import { Alert, Col, Form } from 'react-bootstrap';
import DatePicker from 'react-datepicker';

import Button from '../../components/Button';
import If from '../../components/If';
import Modal from '../../components/Modal';
import { CadernoLPMAAssinatura } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';

interface AssinaturaModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (assinatura: CadernoLPMAAssinatura) => void | Promise<void>;
	initialData?: CadernoLPMAAssinatura;
	cadernoLpmaId?: number | null;
}

const CANVAS_WIDTH = 500;
const CANVAS_HEIGHT = 160;

const AssinaturaModal: React.FC<AssinaturaModalProps> = ({
	show,
	onHide,
	onSubmit,
	initialData,
	cadernoLpmaId,
}) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [isDrawing, setIsDrawing] = useState(false);
	const [nomeColaborador, setNomeColaborador] = useState('');
	const [cargoColaborador, setCargoColaborador] = useState('');
	const [dataAssinatura, setDataAssinatura] = useState<any>(new Date());
	const [assinaturaBase64, setAssinaturaBase64] = useState<string | null>(null);
	const [message, setMessage] = useState<string | null>(null);

	const configurarCanvas = () => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext('2d');
		if (!canvas || !ctx) {
			return;
		}
		ctx.strokeStyle = '#000';
		ctx.lineWidth = 2;
		ctx.lineCap = 'round';
	};

	const limparCanvas = () => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext('2d');
		if (!canvas || !ctx) {
			return;
		}
		ctx.clearRect(0, 0, canvas.width, canvas.height);
		setAssinaturaBase64(null);
		setMessage(null);
	};

	const desenharImagemSalva = (base64: string) => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext('2d');
		if (!canvas || !ctx) {
			return;
		}
		const img = new Image();
		img.onload = () => {
			ctx.clearRect(0, 0, canvas.width, canvas.height);
			const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
			const x = (canvas.width - img.width * scale) / 2;
			const y = (canvas.height - img.height * scale) / 2;
			ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
		};
		img.src = base64;
	};

	useEffect(() => {
		if (!show) {
			return;
		}
		setNomeColaborador(initialData?.nomeColaborador ?? '');
		setCargoColaborador(initialData?.cargoColaborador ?? '');
		setDataAssinatura(initialData?.dataAssinatura ? new Date(initialData.dataAssinatura) : new Date());
		setAssinaturaBase64(initialData?.assinaturaBase64 ?? null);
		setMessage(null);
		setTimeout(() => {
			configurarCanvas();
			if (initialData?.assinaturaBase64) {
				desenharImagemSalva(initialData.assinaturaBase64);
			} else {
				limparCanvas();
			}
		}, 0);
	}, [show, initialData]);

	const getPosition = (
		e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
	) => {
		const canvas = canvasRef.current;
		const rect = canvas?.getBoundingClientRect();
		if (!rect) {
			return null;
		}
		const point = 'touches' in e ? e.touches[0] : e;
		return {
			x: point.clientX - rect.left,
			y: point.clientY - rect.top,
		};
	};

	const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
		e.preventDefault();
		const pos = getPosition(e);
		const ctx = canvasRef.current?.getContext('2d');
		if (!pos || !ctx) {
			return;
		}
		ctx.beginPath();
		ctx.moveTo(pos.x, pos.y);
		setIsDrawing(true);
	};

	const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
		e.preventDefault();
		if (!isDrawing) {
			return;
		}
		const pos = getPosition(e);
		const ctx = canvasRef.current?.getContext('2d');
		if (!pos || !ctx) {
			return;
		}
		ctx.lineTo(pos.x, pos.y);
		ctx.stroke();
		setAssinaturaBase64(null);
	};

	const stopDrawing = (e?: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
		e?.preventDefault();
		setIsDrawing(false);
	};

	const canvasEstaVazio = () => {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext('2d');
		if (!canvas || !ctx) {
			return true;
		}
		const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
		return !imageData.data.some((channel) => channel !== 0);
	};

	const uploadImagem = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) {
			return;
		}
		if (!file.type.startsWith('image/')) {
			setMessage('Selecione um arquivo de imagem válido.');
			return;
		}
		if (file.size > 5 * 1024 * 1024) {
			setMessage('A imagem deve ter no máximo 5MB.');
			return;
		}

		const reader = new FileReader();
		reader.onload = (event) => {
			const img = new Image();
			img.onload = () => {
				const canvas = canvasRef.current;
				const ctx = canvas?.getContext('2d');
				if (!canvas || !ctx) {
					return;
				}
				ctx.clearRect(0, 0, canvas.width, canvas.height);
				const scale = Math.min(canvas.width / img.width, canvas.height / img.height);
				const x = (canvas.width - img.width * scale) / 2;
				const y = (canvas.height - img.height * scale) / 2;
				ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
				setAssinaturaBase64(null);
				setMessage(null);
			};
			img.src = event.target?.result as string;
		};
		reader.readAsDataURL(file);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		const nome = nomeColaborador.trim();
		if (!nome) {
			setMessage('Informe o nome do colaborador.');
			return;
		}
		if (canvasEstaVazio()) {
			setMessage('Desenhe ou carregue uma imagem da assinatura antes de salvar.');
			return;
		}

		const base64String = assinaturaBase64 ?? canvasRef.current?.toDataURL('image/png') ?? null;
		await onSubmit({
			...initialData,
			id: initialData?.id ?? 0,
			cadernoLPMAId: initialData?.cadernoLPMAId ?? cadernoLpmaId ?? 0,
			nomeColaborador: nome,
			cargoColaborador: cargoColaborador.trim() || null,
			dataAssinatura,
			assinaturaBase64: base64String,
		});
	};

	return (
		<Modal
			show={show}
			onHide={onHide}
			title={STRINGS_CADERNOLPMA.ASSINATURAS_MODAL_TITLE}
			bodyClassName="caderno-lpma-modal-body"
		>
			<Form onSubmit={handleSubmit} autoComplete="off">
				<If condition={!!message}>
					<Alert variant="danger" onClose={() => setMessage(null)} dismissible>
						{message}
					</Alert>
				</If>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={6}>
						<Form.Label>Nome do colaborador</Form.Label>
						<Form.Control
							value={nomeColaborador}
							onChange={(e) => setNomeColaborador(e.target.value)}
							placeholder="Informe o nome do colaborador"
							maxLength={150}
						/>
					</Form.Group>
					<Form.Group as={Col} md={3}>
						<Form.Label>Cargo</Form.Label>
						<Form.Control
							value={cargoColaborador}
							onChange={(e) => setCargoColaborador(e.target.value)}
							placeholder="Cargo"
							maxLength={150}
						/>
					</Form.Group>
					<Form.Group as={Col} md={3}>
						<Form.Label>Data assinatura</Form.Label>
						<DatePicker
							selected={dataAssinatura}
							onChange={setDataAssinatura}
							className="input-custom-to-date"
							locale="pt-BR"
							dateFormat="dd/MM/yyyy"
							placeholderText="Selecionar a data"
						/>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Assinatura</Form.Label>
						<div
							style={{
								border: '2px dashed #6c757d',
								borderRadius: '8px',
								backgroundColor: '#fff',
								padding: '10px',
								maxWidth: `${CANVAS_WIDTH + 24}px`,
							}}
						>
							<canvas
								ref={canvasRef}
								width={CANVAS_WIDTH}
								height={CANVAS_HEIGHT}
								onMouseDown={startDrawing}
								onMouseMove={draw}
								onMouseUp={stopDrawing}
								onMouseLeave={stopDrawing}
								onTouchStart={startDrawing}
								onTouchMove={draw}
								onTouchEnd={stopDrawing}
								style={{
									display: 'block',
									cursor: 'crosshair',
									width: '100%',
									maxWidth: `${CANVAS_WIDTH}px`,
									height: 'auto',
									touchAction: 'none',
								}}
							/>
						</div>
						<Form.Text className="text-muted">
							Use o mouse ou toque na tela para desenhar a assinatura.
						</Form.Text>
					</Form.Group>
				</Form.Row>
				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Ou carregue uma imagem</Form.Label>
						<Form.Control type="file" accept="image/*" onChange={uploadImagem} style={{ maxWidth: '500px' }} />
					</Form.Group>
				</Form.Row>
				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Limpar" contained={false} type="button" onClick={limparCanvas} />
					<Button title="Salvar" contained type="submit" />
				</div>
			</Form>
		</Modal>
	);
};

export default AssinaturaModal;
